import { memo, useState } from 'react';
import {
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

import useAxiosPrivate from 'hooks/useAxiosPrivate';

// action -> o'qiladigan yorliq (ContractHistorySection.jsx dagi bilan bir xil naqsh)
const ACTION_LABELS = {
  block_id: 'Blok',
  padezd_id: 'Padezd',
  stage: 'Qavat',
  number: 'Uy raqami',
  rooms: 'Xonalar soni',
  square: 'Maydon',
  total_square: 'Jami maydon',
  status: 'Holat',
  contract_id: 'Shartnoma',
  ADDCONTRACT: 'Shartnomaga biriktirildi',
  REMOVECONTRACT: 'Shartnomadan olib tashlandi',
  isvalute: 'Valyuta',
  isrepaired: "Ta'mir holati",
  islive: 'Turar/Noturar',
  deal_price: 'Kelishilgan narx (1m²)',
  start: "Boshlang'ich to'lov (%)",
  place: 'Joylashuv',
  terassa: 'Terassa maydoni',
  terassa_price: 'Terassa narxi',
  terassa_price_repaired: "Terassa narxi (ta'mirli)",
  totalsum: 'Umumiy summa',
  issquareprice: 'm² bo\'yicha narx',
  plan_id: 'Reja',
  image: 'Rasm',
};

const formatNumberPart = (part) => {
  const trimmed = part.trim();
  if (!/^-?\d+(\.\d+)?$/.test(trimmed)) return part;
  const [int, dec] = trimmed.split('.');
  const formatted = int.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  return dec && !/^0+$/.test(dec) ? `${formatted}.${dec}` : formatted;
};

const formatValue = (value) =>
  value
    .split(' → ')
    .map(formatNumberPart)
    .join(' → ');

/**
 * "Uy tarixi" tugmasi bosilganda ochiladigan oyna — ContractHistorySection.jsx
 * bilan bir xil ma'lumot, lekin alohida modal ko'rinishida.
 */
const HomeHistoryModal = ({ open, setOpen, homeId }) => {
  const { t } = useTranslation();
  const axiosPrivate = useAxiosPrivate();
  const [page, setPage] = useState(1);

  const handleClose = () => {
    setOpen(false);
    setPage(1);
  };

  const { data, isFetching, isError } = useQuery({
    queryKey: ['homeHistories', homeId, page],
    queryFn: async () =>
      (
        await axiosPrivate.get(
          `/admin/home/histories?home_id=${homeId}&page=${page}`
        )
      ).data?.data,
    enabled: open && !!homeId,
    keepPreviousData: true,
    retry: false,
  });

  const rows = data?.data || [];
  const total = data?.total || 0;
  const pageCount = Math.ceil(total / (data?.per_page || 20)) || 1;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby='home-history-modal-title'
      maxWidth='md'
      fullWidth
    >
      <DialogTitle id='home-history-modal-title'>
        <span className='pr-5'>
          <i className='bi bi-clock-history mr-2' />
          {t('shaxmatka.homeDetail.history.title')} ({total})
        </span>
        <div className='close-btn-wrapper'>
          <IconButton variant='onlyIcon' color='primary' onClick={handleClose}>
            <i className='bi bi-x' />
          </IconButton>
        </div>
      </DialogTitle>

      <DialogContent>
        {isError ? (
          <div className='text-red-500 text-xs text-center py-4'>
            <i className='bi bi-exclamation-triangle mr-1' />
            Tarixni yuklab bo'lmadi — migratsiya ishga tushirilganini
            tekshiring (home_histories jadvali)
          </div>
        ) : isFetching && rows.length === 0 ? (
          <div className='flex justify-center py-6'>
            <CircularProgress size={26} />
          </div>
        ) : rows.length === 0 ? (
          <div className='text-gray-400 text-sm text-center py-6'>
            {t('common.global.noDataFound')}
          </div>
        ) : (
          <TableContainer className='!shadow-none !border !border-gray-200 !rounded-xl'>
            <Table size='small'>
              <TableHead>
                <TableRow>
                  {[
                    t('common.fields.date'),
                    t('common.fields.staff'),
                    t('shaxmatka.homeDetail.history.action'),
                    t('shaxmatka.homeDetail.history.value'),
                  ].map((label, i) => (
                    <TableCell
                      key={`hh-${i}`}
                      align='left'
                      sx={{ backgroundColor: 'rgb(var(--c-gray-50))', fontSize: '13px' }}
                    >
                      {label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row?.id} hover tabIndex={-1}>
                    <TableCell className='whitespace-nowrap'>
                      {row?.created_at
                        ? moment(row.created_at).format('DD.MM.YYYY HH:mm:ss')
                        : '—'}
                    </TableCell>
                    <TableCell>{row?.user?.name || '—'}</TableCell>
                    <TableCell>
                      <span className='inline-flex items-center rounded-md text-[12px] leading-none font-medium px-2.5 py-1 whitespace-nowrap ring-1 ring-inset ring-black/5 bg-gray-100 text-gray-600'>
                        {ACTION_LABELS[row?.action] || row?.action}
                      </span>
                    </TableCell>
                    <TableCell>
                      {row?.value ? formatValue(row.value) : '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {pageCount > 1 && (
          <div className='flex justify-center mt-3 pb-1'>
            <Pagination
              count={pageCount}
              page={page}
              onChange={(e, v) => setPage(v)}
              shape='rounded'
              color='primary'
              disabled={isFetching}
            />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default memo(HomeHistoryModal);
